import { AiBrowseResultCache } from "@/types/ai/aiBrowse";
import { AiCacheResult } from "@/types/ai/aiSearch";
import { AiResultGetDto, AiResultItemGetDto } from "@/types/ai/aiShared";
import { Controllers } from "@infoland/iprova-api";
import { ActionTree, GetterTree, MutationTree } from "vuex";

interface State {
    clientPageSize: number;
    serverPageSize: number;
    browseResults: AiBrowseResultCache;
}

const state: State = {
    clientPageSize: 25,
    serverPageSize: 50,
    browseResults: {} as AiBrowseResultCache
};

/** ===================================================================================================
 *  ↓ Utility functions
 *  ===================================================================================================
 */

/**
 * Create simple search query hash to cache the search results
 */
const encode = (queryObject: object): string => {
    const query = JSON.stringify(queryObject);
    return query;
};

/** ===================================================================================================
 *  ↓ Store (Cache) Functions
 *  ===================================================================================================
 */
const getters: GetterTree<State, any> = {
    getItemsToShow: (state: State) => (portalId: number, collectionId: number | null): number => {
        const encodedKey = encode({ portalId: portalId, collectionId: collectionId });
        if (state.browseResults[encodedKey] == null) return undefined;
        return state.browseResults[encodedKey].items_to_show;
    },
    areAllItemsLoaded: (state: State) => (portalId: number, collectionId: number | null): boolean => {
        const encodedKey = encode({ portalId: portalId, collectionId: collectionId });
        if (state.browseResults[encodedKey] == null) return false;
        return state.browseResults[encodedKey].total_items >= state.browseResults[encodedKey].items_to_show;
    },
    getServerPageSize: (state: State) => state.serverPageSize,
    getClientPageSize: (state: State) => state.clientPageSize,
    // get the collection ids from the browse results for this current filter
    getCollectionIds: (state: State) => (portalId: number, collectionId: number | null): number[] => {
        const encodedKey = encode({ portalId: portalId, collectionId: collectionId });
        if (state.browseResults[encodedKey] == null) return [];
        return state.browseResults[encodedKey].collection_ids;
    }
};

const mutations: MutationTree<State> = {
    cacheResults: (state: State, payload: { portalId: number; collectionId: number | null; itemsLoaded: number | null; response: AiCacheResult }) => {
        const encodedKey = encode({ portalId: payload.portalId, collectionId: payload.collectionId });
        if (payload.itemsLoaded == null) state.browseResults[encodedKey] = payload.response;
        else state.browseResults[encodedKey].items = [...state.browseResults[encodedKey].items, ...payload.response.items];
    },
    setItemsToShow(state: State, payload: { portalId: number; collectionId: number | null; itemsToShow: number }) {
        const encodedKey = encode({ portalId: payload.portalId, collectionId: payload.collectionId });
        const browseResults = state.browseResults[encodedKey];
        if (browseResults == null) return;
        const loadedAllResults =
            browseResults != null &&
            (browseResults.items_to_show > browseResults.items.length || browseResults.items.length == 0) &&
            browseResults.total_items >= payload.itemsToShow;

        if (loadedAllResults) return;
        //set the items to items_to_show, but if the items_to_show is more than the total items, set it to the total items
        state.browseResults[encodedKey].items_to_show = Math.min(payload.itemsToShow, browseResults.items.length);
    }
};

const actions: ActionTree<State, any> = {
    loadResults: async (
        { commit, getters },
        payload: { portalId: number; collectionId: number | null; itemsLoaded: number; disableCache: boolean }
    ): Promise<AiResultItemGetDto[]> => {
        const encodedQueryKey = encode({ portalId: payload.portalId, collectionId: payload.collectionId });

        // check if the query is already in the cache
        const containsQueryInCache = state.browseResults[encodedQueryKey] != null && state.browseResults[encodedQueryKey].items.length > 0;
        if (containsQueryInCache && !payload.disableCache) {
            return state.browseResults[encodedQueryKey].items;
        }

        //check if all items are loaded
        const allItemsLoaded =
            state.browseResults[encodedQueryKey] != null &&
            state.browseResults[encodedQueryKey].items_to_show >= state.browseResults[encodedQueryKey].total_items;
        if (allItemsLoaded) return [];

        // if not in cache, fetch the results
        let queryParams: any = { use_preview: true, limit: getters.getServerPageSize, portal_id: payload.portalId };
        if (payload.collectionId != null) {
            queryParams.collection_ids = payload.collectionId;
        }

        if (payload.itemsLoaded != null) {
            queryParams.offset = payload.itemsLoaded;
        }

        const response = await Controllers.GenericController.getAsync<AiResultGetDto<AiResultItemGetDto>>(
            `/portals/content_items?${new URLSearchParams(queryParams).toString()}`,
            false
        );

        if (response?.items == null) return [];

        // cache the results that are just fetched
        commit("cacheResults", {
            portalId: payload.portalId,
            collectionId: payload.collectionId,
            itemsLoaded: payload.itemsLoaded,
            response: response
        });

        // return the results
        return response.items;
    }
};

// export the namespace
export const namespace = "browse";

// export the store 'module'
export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters
};
